import { z } from 'zod';

// ─── Validate form schema ─────────────────────────────────────────────────────

const VALIDATE_ANNOTATIONS = [
  'Email',
  'Phone',
  'BusinessNo',
  'NoXss',
  'NoSqlInjection',
  'SafeFileName',
  'KrResidentNo',
  'Password',
] as const;

export const validateFormSchema = z.object({
  annotation: z.enum(VALIDATE_ANNOTATIONS, {
    errorMap: () => ({ message: '검증기를 선택해 주세요' }),
  }),
  value: z
    .string()
    .min(1, '입력값을 입력해 주세요')
    .max(512, '512자를 초과할 수 없습니다'),
});

export type ValidateFormValues = z.infer<typeof validateFormSchema>;

// ─── Mask form schema ─────────────────────────────────────────────────────────
// #419: canonical values — must match BE MaskType enum exactly

const MASK_TYPES = [
  'EMAIL',
  'PHONE_KR',
  'RESIDENT_NO',
  'BUSINESS_NO',
  'CARD_NO',
  'ACCOUNT_NO',
  'ADDRESS_KR',
  'IP_V4',
  'BIRTH_DATE',
  'NAME_KR',
  'JWT',
  'CUSTOM',
] as const;

// #419: LIGHT | DEFAULT | STRICT (no PARTIAL — BE MaskingMode enum 정합)
const MASKING_MODES = ['LIGHT', 'DEFAULT', 'STRICT'] as const;

export const maskFormSchema = z.object({
  type: z.enum(MASK_TYPES, {
    errorMap: () => ({ message: '마스킹 타입을 선택해 주세요' }),
  }),
  mode: z.enum(MASKING_MODES, {
    errorMap: () => ({ message: '마스킹 모드를 선택해 주세요' }),
  }),
  value: z
    .string()
    .min(1, '값을 입력해 주세요')
    .max(512, '512자를 초과할 수 없습니다'),
});

export type MaskFormValues = z.infer<typeof maskFormSchema>;

// ─── Excel form schema ────────────────────────────────────────────────────────

export const excelFormSchema = z.object({
  rows: z
    .number({ invalid_type_error: '숫자를 입력해 주세요' })
    .int('정수를 입력해 주세요')
    .min(1, '최소 1행 이상이어야 합니다')
    .max(10000, '최대 10000행을 초과할 수 없습니다'),
});

export type ExcelFormValues = z.infer<typeof excelFormSchema>;

// ─── Event form schema ────────────────────────────────────────────────────────

export const eventFormSchema = z.object({
  payloadJson: z
    .string()
    .min(1, 'JSON 페이로드를 입력해 주세요')
    .refine((v) => {
      try {
        JSON.parse(v);
        return true;
      } catch {
        return false;
      }
    }, '유효한 JSON 형식이어야 합니다'),
});

export type EventFormValues = z.infer<typeof eventFormSchema>;

// ─── Error form schema ────────────────────────────────────────────────────────

const ERROR_CODES = [
  'COMMON-404-001',
  'COMMON-409-001',
  'COMMON-500-001',
  'AUTH-401-001',
  'AUTH-403-001',
] as const;

export const errorFormSchema = z.object({
  code: z.enum(ERROR_CODES, {
    errorMap: () => ({ message: 'ErrorCode를 선택해 주세요' }),
  }),
  argsStr: z.string().max(256, '256자를 초과할 수 없습니다').default(''),
});

export type ErrorFormValues = z.infer<typeof errorFormSchema>;

// ─── Cache form schema ────────────────────────────────────────────────────────

export const cacheFormSchema = z.object({
  key: z
    .string()
    .min(1, '키를 입력해 주세요')
    .max(128, '128자를 초과할 수 없습니다'),
});

export type CacheFormValues = z.infer<typeof cacheFormSchema>;

// ─── Kafka form schema (C055) ─────────────────────────────────────────────────

export const kafkaFormSchema = z.object({
  message: z
    .string()
    .min(1, '메시지를 입력해 주세요')
    .max(512, '512자를 초과할 수 없습니다'),
});

export type KafkaFormValues = z.infer<typeof kafkaFormSchema>;

// ─── RabbitMQ form schema (C057) ──────────────────────────────────────────────

export const rabbitFormSchema = z.object({
  message: z
    .string()
    .min(1, '메시지를 입력해 주세요')
    .max(512, '512자를 초과할 수 없습니다'),
});

export type RabbitFormValues = z.infer<typeof rabbitFormSchema>;

// ─── Elasticsearch form schemas (C053) ────────────────────────────────────────

export const elasticsearchIndexFormSchema = z.object({
  message: z
    .string()
    .min(1, '메시지를 입력해 주세요')
    .max(512, '512자를 초과할 수 없습니다'),
});

export type ElasticsearchIndexFormValues = z.infer<typeof elasticsearchIndexFormSchema>;

export const elasticsearchSearchFormSchema = z.object({
  query: z
    .string()
    .max(512, '512자를 초과할 수 없습니다'),
});

export type ElasticsearchSearchFormValues = z.infer<typeof elasticsearchSearchFormSchema>;

// ─── MongoDB form schema (C054) ────────────────────────────────────────────────

export const mongoFormSchema = z.object({
  message: z
    .string()
    .min(1, '메시지를 입력해 주세요')
    .max(512, '512자를 초과할 수 없습니다'),
});

export type MongoFormValues = z.infer<typeof mongoFormSchema>;

// ─── Outbox form schema (C056) ─────────────────────────────────────────────────

export const outboxFormSchema = z.object({
  message: z
    .string()
    .min(1, '메시지를 입력해 주세요')
    .max(512, '512자를 초과할 수 없습니다'),
});

export type OutboxFormValues = z.infer<typeof outboxFormSchema>;

// ─── OAuth2/OIDC bridge form schema (C035) ────────────────────────────────────
// username/password 는 선택 입력 — 생략 시 BE 가 데모 계정(demo-username/demo-password) 사용

export const oauth2FormSchema = z.object({
  username: z.string().max(128, '128자를 초과할 수 없습니다').optional().or(z.literal('')),
  password: z.string().max(128, '128자를 초과할 수 없습니다').optional().or(z.literal('')),
});

export type OAuth2FormValues = z.infer<typeof oauth2FormSchema>;

// ─── Response schemas (runtime parse) ────────────────────────────────────────

export const fieldErrorSchema = z.object({
  field: z.string(),
  code: z.string(),
  message: z.string(),
  rejectedValue: z.string(),
});

export const validateResponseSchema = z.object({
  annotation: z.string(),
  value: z.string(),
  passed: z.boolean(),
  fieldErrors: z.array(fieldErrorSchema),
  durationNanos: z.number(),
});

export const maskResponseSchema = z.object({
  original: z.string(),
  masked: z.string(),
  type: z.string(),
  mode: z.string(),
});

export const eventResponseSchema = z.object({
  eventId: z.string(),
  traceId: z.string(),
  source: z.string(),
  listenerCount: z.number(),
  echoedPayload: z.record(z.unknown()),
  occurredAt: z.string(),
});

export const fileUploadResponseSchema = z.object({
  fileId: z.string(),
  originalName: z.string(),
  size: z.number(),
  mimeType: z.string(),
  presignedDownloadUrl: z.string(),
  uploadedAt: z.string(),
  expiresAt: z.string(),
});

export const cacheDemoResponseSchema = z.object({
  key: z.string(),
  value: z.string(),
  durationMs: z.number(),
  cacheHit: z.boolean(),
});

export const moduleNodeSchema = z.object({
  code: z.string(),
  artifact: z.string(),
  version: z.string(),
  nameKo: z.string(),
  nameEn: z.string(),
  since: z.string(),
  demoEndpoint: z.string().nullable(),
  docsAnchor: z.string().nullable(),
  publicApis: z.array(z.string()),
  usedByBiz: z.array(z.string()).optional(),
  // #617 — SUPERSEDED UI. null 은 "대체 없음", 문자열은 후계 코드 또는 "gateway-self".
  supersededBy: z.string().nullable().optional(),
  supersedes: z.array(z.string()).optional(),
});

// ─── Take-this panel data schema (queue #630 · build-time generated) ─────────

export const takeThisYmlSchema = z.union([
  z.object({
    alwaysOn: z.literal(false),
    gateKey: z.string(),
    relatedConfig: z.array(z.string()).optional(),
    hardInject: z.boolean(),
    andGates: z.array(z.string()).optional(),
    note: z.string().optional(),
  }),
  z.object({
    alwaysOn: z.literal(true),
  }),
]);

export const takeThisDepsSchema = z.object({
  codes: z.array(z.string()),
  unresolved: z.array(z.string()),
});

export const takeThisEntrySchema = z.object({
  sourcePath: z.string(),
  specPath: z.string(),
  isGatewayArtifact: z.boolean(),
  yml: takeThisYmlSchema,
  deps: takeThisDepsSchema,
});

export const takeThisDataSchema = z.record(z.string(), takeThisEntrySchema);

export const moduleCatalogResponseSchema = z.object({
  nodes: z.array(moduleNodeSchema),
  totalModules: z.number(),
  currentUserPermissions: z.array(z.string()),
  generatedAt: z.string(),
});

// ─── Download stats schema (queue #662 §UAT-2026-07-09-R662) ────────────────

export const byModuleCountSchema = z.object({
  code: z.string(),
  count: z.number(),
});

export const downloadStatsResponseSchema = z.object({
  byModule: z.array(byModuleCountSchema),
  byKind: z.object({
    MODULE: z.number(),
    BUNDLE: z.number(),
    SCAFFOLD: z.number(),
  }),
  totalDownloads: z.number(),
  topModules: z.array(byModuleCountSchema),
});
